const TOP_LAYER_ATTRIBUTE = 'data-react-aria-top-layer';
const INERT_ATTRIBUTE = 'inert';

const PANE_EDGE_PROPERTIES = {
  left: '--supporting-pane-left',
  top: '--supporting-pane-top',
  right: '--supporting-pane-right',
  bottom: '--supporting-pane-bottom',
} as const;

type PaneEdge = keyof typeof PANE_EDGE_PROPERTIES;

const PANE_EDGES = Object.keys(PANE_EDGE_PROPERTIES) as PaneEdge[];

const regions: HTMLElement[] = [];
let exposingOverlayCount = 0;
let coveringOverlayCount = 0;
let trackingFrame: number | null = null;
let publishedEdges: string | null = null;
const regionListeners = new Set<() => void>();

const notifyRegionListeners = (): void =>
  regionListeners.forEach(listener => listener());

export const subscribeSupportingPaneRegions = (
  listener: () => void,
): (() => void) => {
  regionListeners.add(listener);
  return () => {
    regionListeners.delete(listener);
  };
};

export const hasSupportingPaneRegion = (): boolean => regions.length > 0;

const writePaneEdges = (edges: string[] | null): void => {
  const serializedEdges = edges?.join(' ') ?? null;
  if (serializedEdges === publishedEdges) return;
  publishedEdges = serializedEdges;

  const rootStyle = document.documentElement.style;
  PANE_EDGES.forEach((edge, index) => {
    const value = edges?.[index];
    if (value) rootStyle.setProperty(PANE_EDGE_PROPERTIES[edge], value);
    else rootStyle.removeProperty(PANE_EDGE_PROPERTIES[edge]);
  });
};

const measureActiveRegion = (): string[] | null => {
  const activeRegion = regions.at(-1);
  if (!activeRegion) return null;

  const rect = activeRegion.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  return PANE_EDGES.map(edge => `${rect[edge]}px`);
};

const trackPaneEdges = (): void => {
  writePaneEdges(measureActiveRegion());
  trackingFrame = requestAnimationFrame(trackPaneEdges);
};

const stopTrackingPaneEdges = (): void => {
  if (trackingFrame !== null) cancelAnimationFrame(trackingFrame);
  trackingFrame = null;
  writePaneEdges(null);
};

const syncRegions = (): void => {
  regions.forEach(region => {
    region.toggleAttribute(TOP_LAYER_ATTRIBUTE, exposingOverlayCount > 0);
    region.toggleAttribute(INERT_ATTRIBUTE, coveringOverlayCount > 0);
  });

  if (exposingOverlayCount > 0 && regions.length > 0) {
    if (trackingFrame === null) trackPaneEdges();
  } else {
    stopTrackingPaneEdges();
  }
};

export const registerSupportingPaneRegion = (
  element: HTMLElement,
): (() => void) => {
  regions.push(element);
  syncRegions();
  notifyRegionListeners();

  return () => {
    const index = regions.indexOf(element);
    if (index === -1) return;
    regions.splice(index, 1);
    element.removeAttribute(TOP_LAYER_ATTRIBUTE);
    element.removeAttribute(INERT_ATTRIBUTE);
    syncRegions();
    notifyRegionListeners();
  };
};

const acquireOverlay = (adjustCount: (delta: number) => void) => {
  adjustCount(1);
  syncRegions();

  return () => {
    adjustCount(-1);
    syncRegions();
  };
};

export const exposeSupportingPane = (): (() => void) =>
  acquireOverlay(delta => {
    exposingOverlayCount += delta;
  });

export const coverSupportingPane = (): (() => void) =>
  acquireOverlay(delta => {
    coveringOverlayCount += delta;
  });
